import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Play, X } from "lucide-react";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { SelmaAdminMobileNav, SelmaAdminSidebar } from "@/components/admin/SelmaAdminSidebar";
import { SelmaAiVoiceGenerator } from "@/components/admin/SelmaAiVoiceGenerator";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

// DuyguŞifa "Selma" admin paneli — "Talepler" sayfası. Mobil taraftan
// (POST /api/duygusifa/talep) gelen ozel_meditasyon_talepleri kayıtlarının admin
// tarafından işlenmesi. "Ödeme Linki Gönder" 2026-08-01'den itibaren gerçek mail
// gönderiyor (IBAN/hesap sahibi/banka adı "Ayarlar" sayfasından okunur) — durum
// akışı detayları için bkz. CLAUDE.md → "Selma Admin Paneli" → "Talepler".

type TalepStatus = "pending" | "odeme_bekleniyor" | "odeme_alindi" | "odeme_yapildi" | "meditasyon_olusturuldu";

type Talep = {
  id: string;
  device_id: string | null;
  fiziksel_rahatsizlik: string;
  baskin_duygu: string;
  ozel_odak: string | null;
  status: TalepStatus;
  admin_notu: string | null;
  atanan_meditasyon_url: string | null;
  user_id: string | null;
  created_at: string;
  kullanici: { display_name: string; email: string } | null;
};

const STATUS_META: Record<TalepStatus, { label: string; className: string }> = {
  pending: { label: "Talep Bekliyor", className: "bg-slate-200 text-slate-700" },
  odeme_bekleniyor: { label: "Ödeme Bekleniyor", className: "bg-amber-100 text-amber-700" },
  odeme_alindi: { label: "Ödeme Alındı", className: "bg-blue-100 text-blue-700" },
  odeme_yapildi: { label: "Ödeme Yapıldı", className: "bg-blue-100 text-blue-700" },
  meditasyon_olusturuldu: { label: "Meditasyon Oluşturuldu", className: "bg-green-100 text-green-700" },
};

const FILTER_CHIPS: { value: TalepStatus | "all"; label: string }[] = [
  { value: "all", label: "Tümü" },
  { value: "pending", label: "Talep Bekliyor" },
  { value: "odeme_bekleniyor", label: "Ödeme Bekleniyor" },
  { value: "odeme_alindi", label: "Ödeme Alındı" },
  { value: "odeme_yapildi", label: "Ödeme Yapıldı" },
  { value: "meditasyon_olusturuldu", label: "Meditasyon Oluşturuldu" },
];

async function fetchTalepler(): Promise<Talep[]> {
  const res = await adminFetch("/api/admin/selma/talepler");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Talepler alınamadı.");
  return Array.isArray(json?.data) ? json.data : [];
}

async function updateDurum(payload: { id: string; status?: TalepStatus; admin_notu?: string }): Promise<void> {
  const { id, ...body } = payload;
  const res = await adminFetch(`/api/admin/selma/talepler/${encodeURIComponent(id)}/durum`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Durum güncellenemedi.");
}

async function assignMeditasyon(payload: { id: string; ses_url: string }): Promise<void> {
  const res = await adminFetch(`/api/admin/selma/talepler/${encodeURIComponent(payload.id)}/meditasyon`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ses_url: payload.ses_url }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Meditasyon atanamadı.");
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminSelmaTalepler() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const [filter, setFilter] = useState<TalepStatus | "all">("all");
  const [notuDraft, setNotuDraft] = useState<Record<string, string>>({});
  const [meditasyonModalId, setMeditasyonModalId] = useState<string | null>(null);
  const [manualSesUrl, setManualSesUrl] = useState("");
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const [audio] = useState(() => (typeof Audio !== "undefined" ? new Audio() : null));
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["selma-talepler"],
    queryFn: fetchTalepler,
    enabled: authenticated,
    staleTime: 0,
  });

  const durumMutation = useMutation({
    mutationFn: updateDurum,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-talepler"], exact: true });
    },
  });

  const meditasyonMutation = useMutation({
    mutationFn: assignMeditasyon,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["selma-talepler"], exact: true });
      setMeditasyonModalId(null);
      setManualSesUrl("");
    },
  });

  function handlePlay(url: string) {
    if (!audio) return;
    if (playingUrl === url) {
      audio.pause();
      audio.currentTime = 0;
      setPlayingUrl(null);
    } else {
      audio.src = url;
      audio.play();
      setPlayingUrl(url);
      audio.onended = () => setPlayingUrl(null);
    }
  }

  function handleOdemeLinkiGonder(talep: Talep) {
    const hedef = talep.kullanici?.email ?? "kullanıcının e-postasına";
    if (!confirm(`${hedef} adresine gerçek bir ödeme bilgisi maili gönderilecek. Devam edilsin mi?`)) return;

    durumMutation.mutate(
      { id: talep.id, status: "odeme_bekleniyor" },
      {
        onSuccess: () => {
          setSentMessage(`Ödeme linki ${hedef} adresine gönderildi.`);
          setTimeout(() => setSentMessage(null), 5000);
        },
      },
    );
  }

  const items = listQuery.data ?? [];
  const visibleItems = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((t) => t.status === filter);
  }, [items, filter]);

  const modalTalep = items.find((t) => t.id === meditasyonModalId) ?? null;

  if (sessionQuery.isLoading) {
    return <div className="p-8 text-sm text-slate-500">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full bg-slate-100/60">
      <SelmaAdminSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="text-base font-semibold text-slate-900 sm:text-lg">Selma — Talepler</div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
              Çıkış
            </Button>
          </div>
          <div className="mt-3">
            <SelmaAdminMobileNav />
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {listQuery.isLoading ? (
            <div className="text-sm text-slate-500">Yükleniyor...</div>
          ) : listQuery.isError ? (
            <div className="text-sm text-destructive">{(listQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div>
                <div className="text-2xl font-extrabold tracking-tight text-slate-900">Kişiye Özel Meditasyon Talepleri</div>
                <div className="mt-1 text-sm text-slate-500">Mobil uygulamadan gelen talepleri ödeme ve meditasyon üretim akışına göre yönet.</div>
              </div>

              {sentMessage && (
                <div className="mt-4 rounded-xl border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-700">{sentMessage}</div>
              )}

              {(durumMutation.isError || meditasyonMutation.isError) && (
                <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  {((durumMutation.error ?? meditasyonMutation.error) as Error)?.message}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                {FILTER_CHIPS.map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    onClick={() => setFilter(chip.value)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
                      filter === chip.value ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-3">
                {visibleItems.map((talep) => {
                  const meta = STATUS_META[talep.status];
                  return (
                    <div key={talep.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-bold text-slate-900">
                              {talep.kullanici?.display_name || talep.kullanici?.email || talep.device_id || "Bilinmeyen kullanıcı"}
                            </span>
                            <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-bold", meta.className)}>{meta.label}</span>
                          </div>
                          {talep.kullanici?.email && (
                            <div className="mt-0.5 text-xs text-slate-500">{talep.kullanici.email}</div>
                          )}
                          <div className="mt-2 text-sm text-slate-700">
                            <span className="font-semibold">Fiziksel rahatsızlık:</span> {talep.fiziksel_rahatsizlik}
                          </div>
                          <div className="mt-1 text-sm text-slate-700">
                            <span className="font-semibold">Baskın duygu:</span> {talep.baskin_duygu}
                          </div>
                          {talep.ozel_odak && (
                            <div className="mt-1 text-sm text-slate-700">
                              <span className="font-semibold">Özel odak:</span> {talep.ozel_odak}
                            </div>
                          )}
                          <div className="mt-2 text-xs text-slate-400">{formatDate(talep.created_at)}</div>
                        </div>

                        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
                          {talep.status === "pending" && (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                className="rounded-full"
                                disabled={durumMutation.isPending}
                                onClick={() => durumMutation.mutate({ id: talep.id, status: "odeme_alindi" })}
                              >
                                Ödeme Alındı
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                disabled={durumMutation.isPending}
                                onClick={() => handleOdemeLinkiGonder(talep)}
                              >
                                Ödeme Linki Gönder
                              </Button>
                            </>
                          )}

                          {talep.status === "odeme_bekleniyor" && (
                            <Button
                              type="button"
                              size="sm"
                              className="rounded-full"
                              disabled={durumMutation.isPending}
                              onClick={() => durumMutation.mutate({ id: talep.id, status: "odeme_yapildi" })}
                            >
                              Ödemeyi Manuel Onayla
                            </Button>
                          )}

                          {(talep.status === "odeme_alindi" || talep.status === "odeme_yapildi") && (
                            <Button
                              type="button"
                              size="sm"
                              className="rounded-full"
                              onClick={() => {
                                setManualSesUrl("");
                                setMeditasyonModalId(talep.id);
                              }}
                            >
                              Meditasyon Oluştur
                            </Button>
                          )}

                          {talep.status === "meditasyon_olusturuldu" && talep.atanan_meditasyon_url && (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handlePlay(talep.atanan_meditasyon_url!)}
                                className="grid h-8 w-8 place-items-center rounded-xl bg-emerald-600/10 text-emerald-700 hover:bg-emerald-600/20"
                                title="Dinle"
                              >
                                <Play className="h-3.5 w-3.5" />
                              </button>
                              <a
                                href={talep.atanan_meditasyon_url}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200"
                                title="İndir"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        <input
                          className="h-9 w-full max-w-sm rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500/30"
                          placeholder="Admin notu..."
                          value={notuDraft[talep.id] ?? talep.admin_notu ?? ""}
                          onChange={(e) => setNotuDraft((d) => ({ ...d, [talep.id]: e.target.value }))}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          disabled={durumMutation.isPending || notuDraft[talep.id] === undefined}
                          onClick={() => durumMutation.mutate({ id: talep.id, admin_notu: notuDraft[talep.id] })}
                        >
                          Notu Kaydet
                        </Button>
                      </div>
                    </div>
                  );
                })}

                {visibleItems.length === 0 && (
                  <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                    Bu filtrede talep yok.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {modalTalep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex flex-shrink-0 items-center justify-between rounded-t-2xl border-b border-slate-200 bg-white p-6 pb-4">
              <div className="font-semibold text-slate-900">Meditasyon Oluştur — {modalTalep.kullanici?.display_name || modalTalep.device_id}</div>
              <button type="button" onClick={() => setMeditasyonModalId(null)}>
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto p-6">
              {meditasyonMutation.isError && (
                <div className="mb-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  {(meditasyonMutation.error as Error).message}
                </div>
              )}
              {meditasyonMutation.isPending && (
                <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">Talebe atanıyor…</div>
              )}

              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Ses Dosyası URL'i</label>
              <input
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500/30"
                value={manualSesUrl}
                onChange={(e) => setManualSesUrl(e.target.value)}
                placeholder="ses dosyası URL'i yapıştır ya da AI ile oluştur..."
              />
              <div className="mt-2 flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  className="rounded-full"
                  disabled={!manualSesUrl.trim() || meditasyonMutation.isPending}
                  onClick={() => meditasyonMutation.mutate({ id: modalTalep.id, ses_url: manualSesUrl.trim() })}
                >
                  Talebe Ata
                </Button>
              </div>

              <div className="mt-4">
                <SelmaAiVoiceGenerator
                  onGenerated={(url) => meditasyonMutation.mutate({ id: modalTalep.id, ses_url: url })}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
